/**
 * Organization context API service.
 *
 * Manages cross-repo context summaries used by the AI planner
 * and surfaced on the Repositories page.
 */

import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export interface RepoContext {
  repoFullName: string;
  summary: string;
  purpose: string;
  techStack: string[];
  dependencies: string[];
  conventions: string[];
  existingServices: string[];
  hasClaudeMd: boolean;
  hasReadme: boolean;
  claudeMdSnippet?: string;
  readmeSnippet?: string;
  updatedAt: string;
}

export interface OrgRepo {
  owner: string;
  repo: string;
  full_name: string;
  description: string | null;
  default_branch: string;
  private: boolean;
  connected: boolean;
  context?: RepoContext;
}

export interface PlanningContextBundle {
  repoSummaries: { repo: string; purpose: string; summary: string; conventions?: string[]; claudeMd?: string }[];
  dependencies: { from: string; to: string; relationship: string }[];
  adrs: { title: string; repo: string }[];
  specs: { name: string; status: string }[];
  targetRepo?: string | null;
}

export interface SyncJobStatus {
  jobId: string;
  status: 'processing' | 'completed' | 'failed';
  synced?: number;
  total?: number;
  error?: string;
}

export const orgContextApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /** List all org repos enriched with context summaries. */
  async listRepos(): Promise<OrgRepo[]> {
    const { data } = await api.get('/org/repos');
    return data;
  },

  /** Sync context for a single repo (fetch CLAUDE.md + README, summarize). */
  async syncRepoContext(owner: string, repo: string): Promise<RepoContext> {
    const { data } = await api.post(`/org/repos/${owner}/${repo}/sync-context`);
    return data;
  },

  /** Start async sync-all job. Returns jobId for polling. */
  async syncAll(): Promise<{ jobId: string }> {
    const { data } = await api.post('/org/sync-all');
    return data;
  },

  /** Poll sync-all job status. */
  async getSyncJobStatus(jobId: string): Promise<SyncJobStatus> {
    const { data } = await api.get(`/org/jobs/${jobId}`);
    return data;
  },

  /** Get pre-computed planning context bundle for the AI planner.
   *  Pass targetRepo to include full CLAUDE.md for that repo and its dependencies. */
  async getPlanningContext(targetRepo?: string): Promise<PlanningContextBundle> {
    const params = targetRepo ? { targetRepo } : undefined;
    const { data } = await api.get('/org/planning-context', { params });
    return data;
  },

  /** Get formatted markdown export for Claude Code memory. */
  async getContextExport(): Promise<string> {
    const { data } = await api.get('/org/context-export');
    return data.markdown;
  },
};
